<template>
<div class="scanner-container">
    <!-- Заголовок -->
    <div class="header">
      <div class="status-badge" :class="statusClass">
        {{ statusText }}
      </div>
    </div>

    <!-- Видоискатель -->
    <div class="viewport">
      <video
        ref="videoRef"
        autoplay
        playsinline
        muted
        class="camera-preview"
      ></video>

      <!-- Простой индикатор обнаружения -->
      <div v-if="documentDetected && !isProcessing" class="detection-indicator">
        <div class="indicator-icon">✅</div>
        <div class="indicator-text">Документ обнаружен</div>
      </div>

      <div v-if="isProcessing" class="processing-overlay">
        <div class="spinner"></div>
        <div class="processing-text">Обработка документа...</div>
      </div>
    </div>

    <!-- Кнопки управления -->
    <div class="controls">
      <button
        @click="startCamera"
        :disabled="cameraActive"
        class="btn btn-primary"
      >
        📷 Запустить камеру
      </button>

      <button
        @click="captureAndProcess"
        :disabled="!documentDetected || isProcessing"
        class="btn btn-success"
      >
        📸 Сделать снимок
      </button>
    </div>

    <!-- Результат с возможностью зумирования -->
    <div v-if="processedDocument" class="result-section">
      <h3>📄 Отсканированный документ</h3>
      <div class="image-viewer">
        <div class="image-container" :class="{ 'zoomed': isZoomed }">
          <img
            :src="processedDocument"
            alt="Processed document"
            @click="toggleZoom"
            class="result-image"
          />
        </div>
        <div class="viewer-controls">
          <button @click="toggleZoom" class="btn btn-small">
            {{ isZoomed ? '🔍 Уменьшить' : '🔍 Увеличить' }}
          </button>
          <button @click="downloadDocument" class="btn btn-small btn-primary">
            💾 Скачать
          </button>
          <button @click="clearResult" class="btn btn-small btn-secondary">
            🗑️ Очистить
          </button>
        </div>
      </div>
    </div>

    <!-- Компактные логи для отладки -->
    <details class="debug-panel">
      <summary>📋 Технические логи ({{ logs.length }})</summary>
      <div class="console-content">
        <div
          v-for="(log, index) in logs.slice(0, 20)"
          :key="index"
          :class="['log-entry', log.type]"
        >
          <span class="log-time">{{ log.time }}</span>
          <span class="log-message">{{ log.message }}</span>
        </div>
      </div>
    </details>
  </div>
</template>

<script lang="ts">
import {
  ref,
  onUnmounted,
  watch,
  nextTick,
} from 'vue';
import { Scanner } from 'scanic';
import type { CornerPoints } from 'scanic';

export default { name: 'ScannerView' };
</script>
<script setup lang="ts">
const videoRef = ref<HTMLVideoElement | null>(null);
const cameraActive = ref(false);
const documentDetected = ref(false);
const isProcessing = ref(false);
const processedDocument = ref<string | null>(null);
const isZoomed = ref(false);
const debugCanvas = ref<HTMLCanvasElement | null>(null);
const showDebugCorners = ref(false);
const detectedDebugCorners = ref<any>(null);
// Статус для отображения пользователю
const statusText = ref('📷 Нажмите "Запустить камеру"');
const statusClass = ref('status-idle');

// Логи для отладки (только важное)
interface LogEntry {
  time: string
  message: string
  type: 'info' | 'success' | 'error'
}
const logs = ref<LogEntry[]>([]);

// Сканер
let scanner: Scanner | null = null;
let mediaStream: MediaStream | null = null;
let detectionInterval: number | null = null;

// ==================== Вспомогательные функции ====================
const addLog = (message: string, type: LogEntry['type'] = 'info') => {
  const now = new Date();
  const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

  logs.value.unshift({ time, message, type });

  // Ограничиваем количество логов
  if (logs.value.length > 50) {
    logs.value.pop();
  }

  console.log(`[${time}] ${message}`);
};

const updateStatus = (text: string, type: string) => {
  statusText.value = text;
  statusClass.value = type;
};

// ==================== Работа с камерой ====================
const startCamera = async () => {
  addLog('Запрос доступа к камере...', 'info');
  updateStatus('🎥 Запрос доступа...', 'status-warning');

  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode: 'environment',
      },
    });

    if (videoRef.value) {
      videoRef.value.srcObject = mediaStream;
      videoRef.value.onloadedmetadata = () => {
        videoRef.value?.play();
        cameraActive.value = true;
        updateStatus('✅ Камера готова', 'status-success');
        addLog(`Камера запущена: ${videoRef.value?.videoWidth}x${videoRef.value?.videoHeight}`, 'success');

        // Инициализируем сканер и начинаем поиск
        initScannerAndStartDetection();
      };
    }
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
    addLog(`Ошибка камеры: ${errMsg}`, 'error');
    updateStatus('❌ Ошибка доступа к камере', 'status-error');
  }
};

// ==================== Инициализация сканера ====================
const initScannerAndStartDetection = async () => {
  addLog('Инициализация сканера...', 'info');
  updateStatus('🔄 Загрузка сканера...', 'status-warning');

  try {
    scanner = new Scanner();
    await scanner.initialize();
    addLog('✅ Сканер готов', 'success');
    updateStatus('🔍 Ищу документ...', 'status-warning');

    // Запускаем цикл детекции
    startDetectionLoop();
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
    addLog(`Ошибка инициализации: ${errMsg}`, 'error');
    updateStatus('❌ Ошибка сканера', 'status-error');
  }
};

// ==================== Захват кадра ====================
const captureCurrentFrame = (): HTMLCanvasElement | null => {
  const video = videoRef.value;
  if (!video || !cameraActive.value || video.readyState !== 4) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
};

// ==================== Обнаружение с отладкой ====================
const detectDocument = async () => {
  if (!scanner || !cameraActive.value) return;

  const canvas = captureHighQualityFrame();
  if (!canvas) return;

  try {
    const result = await scanner.scan(canvas, {
      mode: 'detect',
      maxProcessingDimension: 800,
      lowThreshold: 40, // Более чувствительно
      highThreshold: 120,
      minArea: 10000, // Увеличил минимальную площадь
      debug: true,
    });

    if (result.success && result.corners) {
      if (!documentDetected.value) {
        addLog(`✅ Документ найден! Углы: TL(${Math.round(result.corners.topLeft.x)},${Math.round(result.corners.topLeft.y)})`, 'success');
        updateStatus('✅ Документ найден! Нажмите "Сделать снимок"', 'status-success');
      }
      documentDetected.value = true;

      // Сохраняем углы для отладки
      if (showDebugCorners.value) {
        detectedDebugCorners.value = result.corners;
        drawDebugCorners(canvas, result.corners);
      }
    } else {
      if (documentDetected.value) {
        addLog('Документ пропал из кадра', 'info');
        updateStatus('🔍 Ищу документ...', 'status-warning');
      }
      documentDetected.value = false;
      detectedDebugCorners.value = null;
      if (showDebugCorners.value) {
        clearDebugCanvas();
      }
    }
  } catch (error) {
    documentDetected.value = false;
  }
};

// Отрисовка углов на отладочном canvas
const drawDebugCorners = (sourceCanvas: HTMLCanvasElement, corners: any) => {
  if (!debugCanvas.value || !videoRef.value) return;

  const video = videoRef.value;
  const debugCtx = debugCanvas.value.getContext('2d');
  if (!debugCtx) return;

  // Настраиваем размер отладочного canvas
  debugCanvas.value.width = video.clientWidth;
  debugCanvas.value.height = video.clientHeight;
  debugCanvas.value.style.width = `${video.clientWidth}px`;
  debugCanvas.value.style.height = `${video.clientHeight}px`;

  // Очищаем
  debugCtx.clearRect(0, 0, debugCanvas.value.width, debugCanvas.value.height);

  // Коэффициенты пересчета
  const scaleX = debugCanvas.value.width / sourceCanvas.width;
  const scaleY = debugCanvas.value.height / sourceCanvas.height;

  const points = [
    corners.topLeft, corners.topRight,
    corners.bottomRight, corners.bottomLeft,
  ];

  // Рисуем линии
  debugCtx.beginPath();
  debugCtx.strokeStyle = '#00ff00';
  debugCtx.lineWidth = 3;

  const scaledPoints = points.map((p) => ({
    x: p.x * scaleX,
    y: p.y * scaleY,
  }));

  debugCtx.moveTo(scaledPoints[0].x, scaledPoints[0].y);
  debugCtx.lineTo(scaledPoints[1].x, scaledPoints[1].y);
  debugCtx.lineTo(scaledPoints[2].x, scaledPoints[2].y);
  debugCtx.lineTo(scaledPoints[3].x, scaledPoints[3].y);
  debugCtx.closePath();
  debugCtx.stroke();

  // Рисуем точки
  scaledPoints.forEach((point) => {
    debugCtx.fillStyle = '#ff0000';
    debugCtx.beginPath();
    debugCtx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
    debugCtx.fill();
  });
};

const clearDebugCanvas = () => {
  if (!debugCanvas.value) return;
  const ctx = debugCanvas.value.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, debugCanvas.value.width, debugCanvas.value.height);
  }
};

// ==================== Цикл детекции ====================
const startDetectionLoop = () => {
  if (detectionInterval) clearInterval(detectionInterval);
  detectionInterval = window.setInterval(() => {
    detectDocument();
  }, 200); // 5 раз в секунду
};

const stopDetectionLoop = () => {
  if (detectionInterval) {
    clearInterval(detectionInterval);
    detectionInterval = null;
  }
};
// ==================== Захват кадра в высоком качестве ====================
const captureHighQualityFrame = (): HTMLCanvasElement | null => {
  const video = videoRef.value;
  if (!video || !cameraActive.value || video.readyState !== 4) {
    return null;
  }

  const canvas = document.createElement('canvas');
  // Используем НАТИВНОЕ разрешение видео (максимальное качество)
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  addLog(`Захвачен кадр: ${canvas.width}x${canvas.height}`, 'info');
  return canvas;
};

// ==================== ОСНОВНОЕ: Снимок с расширением границ ====================
const captureAndProcess = async () => {
  if (!documentDetected.value || !scanner) {
    addLog('Нет документа для сканирования', 'error');
    return;
  }

  isProcessing.value = true;
  updateStatus('🔄 Обработка документа...', 'status-warning');
  addLog('📸 Делаю снимок в максимальном качестве...', 'info');

  stopDetectionLoop();

  // Захватываем кадр в ОРИГИНАЛЬНОМ разрешении камеры
  const canvas = captureHighQualityFrame();
  if (!canvas) {
    addLog('Не удалось захватить кадр', 'error');
    isProcessing.value = false;
    startDetectionLoop();
    return;
  }

  addLog(`Обработка изображения ${canvas.width}x${canvas.height}...`, 'info');

  try {
    // Сначала находим углы с более чувствительными настройками
    const detectResult = await scanner.scan(canvas, {
      mode: 'detect',
      maxProcessingDimension: 1200,
      lowThreshold: 30, // Более низкий порог для захвата слабых границ
      highThreshold: 100,
      minArea: 5000, // Меньшая минимальная площадь
      dilationKernelSize: 5, // Увеличиваем ядро для связывания границ
      debug: true,
    });

    if (!detectResult.success || !detectResult.corners) {
      addLog('Не удалось точно определить границы документа', 'error');
      updateStatus('❌ Ошибка определения границ', 'status-error');
      isProcessing.value = false;
      startDetectionLoop();
      return;
    }

    // Получаем оригинальные углы
    const originalCorners = detectResult.corners;
    addLog(`Оригинальные границы: TL(${Math.round(originalCorners.topLeft.x)},${Math.round(originalCorners.topLeft.y)})`, 'success');

    // РАСШИРЯЕМ ГРАНИЦЫ наружу на 5-10%
    const expandedCorners = expandCorners(
      originalCorners,
      canvas.width,
      canvas.height,
      0.08,
    ); // 8% отступ

    addLog(`Расширенные границы: TL(${Math.round(expandedCorners.topLeft.x)},${Math.round(expandedCorners.topLeft.y)})`, 'success');

    // Создаем временный canvas с расширенными границами
    const expandedCanvas = cropAndExpandCanvas(canvas, originalCorners, expandedCorners);

    // Теперь извлекаем документ с расширенными границами
    const extractResult = await scanner.scan(expandedCanvas, {
      mode: 'extract',
      output: 'dataurl',
      maxProcessingDimension: 2000,
      lowThreshold: 30,
      highThreshold: 100,
      minArea: 5000,
    });

    if (extractResult.success && extractResult.output) {
      processedDocument.value = extractResult.output as string;
      addLog('✅ Документ успешно отсканирован с расширенными границами!', 'success');
      updateStatus('✅ Готово!', 'status-success');
      stopCamera();
    } else {
      // Fallback: пробуем без расширения
      addLog('Пробую без расширения границ...', 'info');
      const fallbackResult = await scanner.scan(canvas, {
        mode: 'extract',
        output: 'dataurl',
        maxProcessingDimension: 2000,
        lowThreshold: 30,
        highThreshold: 100,
        minArea: 5000,
      });

      if (fallbackResult.success && fallbackResult.output) {
        processedDocument.value = fallbackResult.output as string;
        addLog('✅ Документ отсканирован (стандартный режим)', 'success');
        updateStatus('✅ Готово!', 'status-success');
        stopCamera();
      } else {
        addLog('❌ Ошибка извлечения', 'error');
        updateStatus('❌ Ошибка, попробуйте снова', 'status-error');
        startDetectionLoop();
      }
    }
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка';
    addLog(`Ошибка обработки: ${errMsg}`, 'error');
    updateStatus('❌ Ошибка обработки', 'status-error');
    startDetectionLoop();
  } finally {
    isProcessing.value = false;
  }
};

// ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ РАСШИРЕНИЯ ГРАНИЦ ====================

// Расширяет границы наружу на заданный процент
const expandCorners = (
  corners: any,
  imageWidth: number,
  imageHeight: number,
  paddingPercent: number,
) => {
  // Находим центр документа
  const centerX = (
    corners.topLeft.x + corners.topRight.x + corners.bottomRight.x + corners.bottomLeft.x) / 4;
  const centerY = (
    corners.topLeft.y + corners.topRight.y + corners.bottomRight.y + corners.bottomLeft.y) / 4;

  // Функция для расширения точки относительно центра
  const expandPoint = (point: { x: number, y: number }, factor: number) => ({
    x: centerX + (point.x - centerX) * factor,
    y: centerY + (point.y - centerY) * factor,
  });

  // Расширяем все углы на (1 + paddingPercent)
  const factor = 1 + paddingPercent;
  const expanded = {
    topLeft: expandPoint(corners.topLeft, factor),
    topRight: expandPoint(corners.topRight, factor),
    bottomRight: expandPoint(corners.bottomRight, factor),
    bottomLeft: expandPoint(corners.bottomLeft, factor),
  };

  // Ограничиваем границами изображения
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  expanded.topLeft.x = clamp(expanded.topLeft.x, 0, imageWidth);
  expanded.topLeft.y = clamp(expanded.topLeft.y, 0, imageHeight);
  expanded.topRight.x = clamp(expanded.topRight.x, 0, imageWidth);
  expanded.topRight.y = clamp(expanded.topRight.y, 0, imageHeight);
  expanded.bottomRight.x = clamp(expanded.bottomRight.x, 0, imageWidth);
  expanded.bottomRight.y = clamp(expanded.bottomRight.y, 0, imageHeight);
  expanded.bottomLeft.x = clamp(expanded.bottomLeft.x, 0, imageWidth);
  expanded.bottomLeft.y = clamp(expanded.bottomLeft.y, 0, imageHeight);

  return expanded;
};

// Создает новый canvas с расширенной областью документа
const cropAndExpandCanvas = (
  sourceCanvas: HTMLCanvasElement,
  originalCorners: any,
  expandedCorners: any,
) => {
  // Находим bounding box расширенных углов
  const xs = [
    expandedCorners.topLeft.x, expandedCorners.topRight.x,
    expandedCorners.bottomRight.x, expandedCorners.bottomLeft.x,
  ];
  const ys = [
    expandedCorners.topLeft.y, expandedCorners.topRight.y,
    expandedCorners.bottomRight.y, expandedCorners.bottomLeft.y,
  ];

  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  const width = maxX - minX;
  const height = maxY - minY;

  // Создаем новый canvas
  const newCanvas = document.createElement('canvas');
  newCanvas.width = width;
  newCanvas.height = height;

  const ctx = newCanvas.getContext('2d');
  if (!ctx) return sourceCanvas;

  // Копируем область с расширенными границами
  ctx.drawImage(
    sourceCanvas,
    minX,
    minY,
    width,
    height,
    0,
    0,
    width,
    height,
  );

  addLog(`Расширенная область: ${width}x${height} (было ${sourceCanvas.width}x${sourceCanvas.height})`, 'info');

  return newCanvas;
};

// ==================== Остановка камеры ====================
const stopCamera = () => {
  stopDetectionLoop();
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
    mediaStream = null;
  }
  if (videoRef.value) {
    videoRef.value.srcObject = null;
  }
  cameraActive.value = false;
  documentDetected.value = false;
};

// ==================== Работа с результатом ====================
const toggleZoom = () => {
  isZoomed.value = !isZoomed.value;
};

const downloadDocument = () => {
  if (!processedDocument.value) return;

  const link = document.createElement('a');
  link.download = `scanned-document-${Date.now()}.png`;
  link.href = processedDocument.value;
  link.click();
  addLog('Файл скачан', 'success');
};

const clearResult = () => {
  processedDocument.value = null;
  isZoomed.value = false;
  addLog('Результат очищен', 'info');
  updateStatus('🔍 Ищу документ...', 'status-warning');

  // Перезапускаем камеру и детекцию
  startCamera();
};

// ==================== Очистка при размонтировании ====================
onUnmounted(() => {
  stopDetectionLoop();
  if (mediaStream) {
    mediaStream.getTracks().forEach((track) => track.stop());
  }
  addLog('Компонент размонтирован', 'info');
});
</script>

<style scoped>
/* Глобальный контейнер с прокруткой */
.scanner-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-height: 100vh; /* Минимальная высота на весь экран */
  overflow-y: auto;  /* Вертикальная прокрутка */
  display: flex;
  flex-direction: column;
}

/* Заголовок */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
  flex-shrink: 0; /* Не сжимается */
}

h2 {
  margin: 0;
  color: #333;
}

.status-badge {
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.status-idle {
  background: #e9ecef;
  color: #6c757d;
}

.status-warning {
  background: #fff3cd;
  color: #856404;
}

.status-success {
  background: #d4edda;
  color: #155724;
}

.status-error {
  background: #f8d7da;
  color: #721c24;
}

/* Видоискатель - фиксированная пропорция, но не фиксированная высота */
.viewport {
  position: relative;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  /* Соотношение сторон 4:3 для видео */
  aspect-ratio: 4 / 3;
  width: 100%;
}

.camera-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

/* Простой индикатор обнаружения */
.detection-indicator {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(40, 167, 69, 0.95);
  color: white;
  padding: 12px 24px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 16px;
  backdrop-filter: blur(4px);
  animation: pulse 1.5s ease-in-out infinite;
  z-index: 10;
  white-space: nowrap;
}

.indicator-icon {
  font-size: 24px;
}

.indicator-text {
  letter-spacing: 0.5px;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.9;
    transform: translateX(-50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translateX(-50%) scale(1.02);
  }
}

/* Оверлей обработки */
.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing-text {
  margin-top: 16px;
  color: white;
  font-size: 16px;
  font-weight: 500;
}

/* Кнопки */
.controls {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.btn {
  padding: 12px 28px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 40px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #1e7e34;
  transform: translateY(-1px);
}

.btn-small {
  padding: 8px 16px;
  font-size: 14px;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

/* Результат с зумом - будет прокручиваться */
.result-section {
  margin-top: 24px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 16px;
  flex-shrink: 0;
}

.result-section h3 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #28a745;
}

.image-viewer {
  text-align: center;
}

.image-container {
  overflow: auto;
  max-height: 500px;
  margin-bottom: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.image-container.zoomed {
  max-height: 70vh;
}

.result-image {
  max-width: 100%;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s;
  display: block;
  margin: 0 auto;
}

.viewer-controls {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Дебаг панель (сворачиваемая) */
.debug-panel {
  margin-top: 20px;
  background: #1e1e1e;
  border-radius: 8px;
  color: #ddd;
  flex-shrink: 0;
}

.debug-panel summary {
  padding: 10px 15px;
  cursor: pointer;
  font-family: monospace;
  font-size: 13px;
  user-select: none;
}

.debug-panel summary:hover {
  background: #2d2d2d;
}

.console-content {
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  border-top: 1px solid #333;
}

.log-entry {
  padding: 4px 8px;
  border-bottom: 1px solid #333;
  display: flex;
  gap: 10px;
}

.log-time {
  color: #888;
  flex-shrink: 0;
}

.log-message {
  color: #ddd;
  word-break: break-word;
}

.log-entry.success .log-message {
  color: #4caf50;
}

.log-entry.error .log-message {
  color: #f44336;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .scanner-container {
    padding: 12px;
  }

  .btn {
    padding: 10px 20px;
    font-size: 14px;
  }

  .detection-indicator {
    padding: 8px 16px;
    font-size: 14px;
    bottom: 10px;
  }

  .indicator-icon {
    font-size: 20px;
  }

  .result-section {
    padding: 12px;
  }

  .image-container {
    max-height: 350px;
  }

  .image-container.zoomed {
    max-height: 60vh;
  }
}

/* Дополнительно: стили для body на уровне приложения (если нужно глобально) */
:global(body) {
  margin: 0;
  padding: 0;
  overflow-y: auto;
}

:global(#app) {
  min-height: 100vh;
  overflow-y: auto;
}
</style>
